import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import './Notification.css';

/**
 * Notification Component
 *
 * Toast-style feedback notification that slides in from the left,
 * shows a shrinking timer bar, and auto-dismisses after `duration` ms.
 *
 * Three types: success (green), error (red), warning (orange).
 * Uses DS tokens: colors, spacing, radius, transitions.
 *
 * Figma nodes:
 * - Parent:  3998:905
 * - Success: 3998:902
 * - Error:   3998:903
 * - Warning: 3998:904
 */
const Notification = ({
  type = 'success',
  title = '',
  message = '',
  duration = 4000,
  onClose,
  preview = false,
  closeLabel = 'Close notification',
  className = '',
}) => {
  const [phase, setPhase] = useState(preview ? 'visible' : 'entering');
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const remainingRef = useRef(duration);
  const startTimeRef = useRef(null);

  const dismiss = useCallback(() => {
    if (phase === 'leaving' || phase === 'done') return;
    clearTimeout(timerRef.current);

    if (preview) {
      onClose?.();
      return;
    }

    setPhase('leaving');
    setTimeout(() => {
      setPhase('done');
      onClose?.();
    }, 350);
  }, [phase, preview, onClose]);

  /* ── Auto-dismiss timer ── */
  useEffect(() => {
    if (preview || phase !== 'visible') return;

    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(dismiss, remainingRef.current);

    return () => clearTimeout(timerRef.current);
  }, [preview, phase, dismiss]);

  /* ── Transition from entering → visible ── */
  useEffect(() => {
    if (phase === 'entering') {
      const id = setTimeout(() => setPhase('visible'), 400);
      return () => clearTimeout(id);
    }
  }, [phase]);

  /* ── Pause / resume on hover ── */
  const handleMouseEnter = () => {
    if (preview || phase !== 'visible') return;
    setPaused(true);
    clearTimeout(timerRef.current);
    remainingRef.current -= Date.now() - startTimeRef.current;
  };

  const handleMouseLeave = () => {
    if (preview || phase !== 'visible') return;
    setPaused(false);
    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(dismiss, remainingRef.current);
  };

  if (phase === 'done') return null;

  const phaseClass =
    phase === 'entering' ? 'ds-notification--entering' :
    phase === 'leaving' ? 'ds-notification--leaving' :
    phase === 'visible' ? 'ds-notification--visible' : '';

  const previewClass = preview ? 'ds-notification--preview' : '';

  const card = (
    <div
      className={`ds-notification ${phaseClass} ${previewClass} ${className}`.trim()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="alert"
      aria-live="assertive"
    >
      <div className="ds-notification__body">
        {/* Close button */}
        <button
          className="ds-notification__close"
          onClick={dismiss}
          aria-label={closeLabel}
        >
          <X size={20} />
        </button>

        {/* Content */}
        <div className="ds-notification__content">
          {title && (
            <p className="ds-notification__title typo-h4">{title}</p>
          )}

          {message && (
            <div className={`ds-notification__info ds-notification__info--${type}`}>
              <p className={`ds-notification__info-text--${type} typo-body`}>
                {message}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Timer bar */}
      <div
        className={`ds-notification__timer ${
          phase === 'visible' && !preview ? 'ds-notification__timer--running' : ''
        } ${paused ? 'ds-notification__timer--paused' : ''}`}
        style={{ '--ds-notification-duration': `${duration}ms` }}
      />
    </div>
  );

  /* Preview mode renders inline (no portal, no fixed positioning) */
  if (preview) return card;

  /* Live mode: portal into a fixed bottom-left container */
  return ReactDOM.createPortal(
    <div className="ds-notification-container">{card}</div>,
    document.body,
  );
};

Notification.propTypes = {
  type: PropTypes.oneOf(['success', 'error', 'warning']),
  title: PropTypes.string,
  message: PropTypes.string,
  duration: PropTypes.number,
  onClose: PropTypes.func,
  preview: PropTypes.bool,
  /** Screen-reader name of the close (X) button */
  closeLabel: PropTypes.string,
  className: PropTypes.string,
};

export default Notification;
